import { useState, useEffect } from 'react';
import { ModuleLink as Link } from '../../components/ModuleLink';
import Layout from '../../components/Layout';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { fmtDate, getActionConfig, isOverdue, isThisMonth } from '../../utils';
import type { ChangeRequest, ActionItem } from '../../types';

// ── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  meta,
  variant,
}: {
  label: string;
  value: number | string;
  meta?: string;
  variant?: string;
}) {
  return (
    <div className={`stat-card${variant ? ` stat-card--${variant}` : ''}`}>
      <div className='stat-card__label'>{label}</div>
      <div className='stat-card__value'>{value}</div>
      {meta && <div className='stat-card__meta'>{meta}</div>}
    </div>
  );
}

// ── Action item helpers ────────────────────────────────────────────────────────
function getActionTypeConfig(type: ActionItem['type']): {
  label: string;
  icon: string;
  buttonLabel: string;
} {
  switch (type) {
    case 'stakeholder-response':
      return {
        label: 'Stakeholder Response',
        icon: 'fa-comment-dots',
        buttonLabel: 'Submit Response',
      };
    case 'plan-signoff':
      return {
        label: 'Plan Sign-Off',
        icon: 'fa-signature',
        buttonLabel: 'Review & Sign Off',
      };
    case 'approval':
      return {
        label: 'Approval Required',
        icon: 'fa-gavel',
        buttonLabel: 'Review & Decide',
      };
    case 'walkthrough':
      return {
        label: 'Walkthrough — Submit Decision',
        icon: 'fa-person-chalkboard',
        buttonLabel: 'Submit Final Decision',
      };
    case 'sponsor-closure':
      return {
        label: 'Closure Sign-Off',
        icon: 'fa-stamp',
        buttonLabel: 'Review & Close',
      };
    case 'survey':
      return {
        label: 'Post-Implementation Survey',
        icon: 'fa-chart-bar',
        buttonLabel: 'Complete Survey',
      };
    default:
      return { label: 'Action Required', icon: 'fa-bell', buttonLabel: 'View' };
  }
}

function getActionUrl(item: ActionItem): string {
  switch (item.type) {
    case 'stakeholder-response':
      return `/stakeholder-response?id=${encodeURIComponent(item.responseId!)}&cid=${encodeURIComponent(item.changeUUID)}`;
    case 'plan-signoff':
      return `/plan-signoff?id=${encodeURIComponent(item.responseId!)}&cid=${encodeURIComponent(item.changeUUID)}`;
    case 'approval':
    case 'walkthrough':
      return `/review-approve?cid=${encodeURIComponent(item.changeUUID)}`;
    case 'sponsor-closure':
      return `/sponsor-closure?cid=${encodeURIComponent(item.changeUUID)}`;
    case 'survey':
      return `/survey?id=${encodeURIComponent(item.logId!)}`;
    default:
      return `/manage-change?cid=${encodeURIComponent(item.changeUUID)}`;
  }
}

// ── Action items panel ─────────────────────────────────────────────────────────
function ActionItemsPanel({ actions }: { actions: ActionItem[] }) {
  if (actions.length === 0) return null;
  return (
    <div
      className='section-panel'
      style={{ marginBottom: 24, borderLeft: '3px solid var(--warn)' }}
    >
      <div className='section-panel__header'>
        <h2 className='section-panel__title'>
          <i
            className='fa-solid fa-bell'
            aria-hidden='true'
            style={{ marginRight: 8, color: 'var(--warn)' }}
          />
          Your Action Items
        </h2>
        <span className='badge badge--warn'>{actions.length} pending</span>
      </div>
      <div>
        {actions.map((item, i) => {
          const config = getActionTypeConfig(item.type);
          const url = getActionUrl(item);
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 20px',
                borderTop: i > 0 ? '1px solid var(--border)' : undefined,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--warn-light)',
                  color: 'var(--warn)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className={`fa-solid ${config.icon}`} aria-hidden='true' />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--ink-dim)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: 2,
                  }}
                >
                  {config.label}
                </div>
                <div
                  style={{
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    overflow: 'hidden',
                  }}
                >
                  <span className='change-id-mono' style={{ flexShrink: 0 }}>
                    {item.changeId}
                  </span>
                  <span className='text-muted' style={{ flexShrink: 0 }}>
                    ·
                  </span>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.changeName}
                  </span>
                </div>
              </div>
              <Link
                to={url}
                className='btn btn--warn btn--sm'
                style={{ flexShrink: 0 }}
              >
                {config.buttonLabel}
                <i className='fa-solid fa-arrow-right' aria-hidden='true' />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── My Changes view ────────────────────────────────────────────────────────────
function MyChangesView({
  email,
  onSwitchToCompliance,
  isComplianceOfficer,
}: {
  email: string;
  onSwitchToCompliance: () => void;
  isComplianceOfficer: boolean;
}) {
  const api = useApiClient();
  const { showToast } = useToast();
  const [records, setRecords] = useState<ChangeRequest[]>([]);
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<ChangeRequest[]>(`/changes?leadEmail=${encodeURIComponent(email)}`)
      .then(setRecords)
      .catch((err) => showToast((err as Error).message, 'error'))
      .finally(() => setLoading(false));

    api
      .get<ActionItem[]>(`/actions?email=${encodeURIComponent(email)}`)
      .then(setActions)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const active = records.filter(
    (r) => !['Closed', 'Rejected'].includes(r.status),
  );
  const awaitingAction = records.filter((r) =>
    [
      'Awaiting Stakeholder Notification',
      'Submitted',
      'Planning',
      'Plan Submitted',
      'Pending Sign-Off',
      'Ready for Approval',
      'Walkthrough Requested',
      'Rejection Review',
      'Implementation Complete',
      'Under Review',
      'Pending Sponsor Closure',
    ].includes(r.status),
  );
  const pendingApproval = records.filter((r) =>
    ['Ready for Approval', 'Walkthrough Requested', 'Plan Submitted'].includes(
      r.status,
    ),
  );

  return (
    <>
      <div className='page-header'>
        <div className='page-header__title'>
          <h1>Change Management</h1>
          <p>
            Track and manage your change requests through the approval
            lifecycle.
          </p>
        </div>
        <div className='page-header__actions'>
          {isComplianceOfficer && (
            <button
              className='btn btn--secondary'
              onClick={onSwitchToCompliance}
            >
              <i className='fa-solid fa-shield-halved' aria-hidden='true' />
              Compliance View
            </button>
          )}
          <Link to='/submit-change' className='btn btn--primary'>
            <i className='fa-solid fa-plus' aria-hidden='true' />
            New Change Request
          </Link>
        </div>
      </div>

      <div className='stat-grid'>
        <StatCard
          label='My Active Changes'
          value={loading ? '…' : active.length}
          meta='Not closed or rejected'
          variant='primary'
        />
        <StatCard
          label='Awaiting My Action'
          value={loading ? '…' : awaitingAction.length}
          meta='Requires your input now'
          variant='warn'
        />
        <StatCard
          label='Pending Approval'
          value={loading ? '…' : pendingApproval.length}
          meta='Awaiting approver decision'
          variant='secondary'
        />
      </div>

      <ActionItemsPanel actions={actions} />

      <div className='section-panel'>
        <div className='section-panel__header'>
          <h2 className='section-panel__title'>My Change Register</h2>
        </div>
        <div
          className='table-wrapper'
          style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}
        >
          <table className='table' aria-label='My change requests'>
            <thead>
              <tr>
                <th scope='col'>Change ID</th>
                <th scope='col'>Change Name</th>
                <th scope='col'>Scope</th>
                <th scope='col'>Stage</th>
                <th scope='col'>Status</th>
                <th scope='col'>Proposed Go-Live</th>
                <th scope='col'>Last Modified</th>
                <th scope='col'></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8}>
                    <div
                      className='loading-state'
                      style={{ padding: '40px 24px' }}
                    >
                      <div
                        className='spinner'
                        role='status'
                        aria-label='Loading'
                      />
                      <span>Loading changes…</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && records.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div
                      className='empty-state-inner'
                      style={{ padding: '48px 24px' }}
                    >
                      <i className='fa-solid fa-inbox' aria-hidden='true' />
                      <p>
                        No change requests found.{' '}
                        <Link to='/submit-change'>
                          Submit your first change
                        </Link>
                        .
                      </p>
                    </div>
                  </td>
                </tr>
              )}
              {records.map((rec) => {
                const action = getActionConfig(rec);
                const overdue = isOverdue(rec);
                return (
                  <tr key={rec.id}>
                    <td data-label="Change ID">
                      <span className='change-id-mono'>{rec.changeId}</span>
                    </td>
                    <td data-label="Change Name">
                      <span className='truncate' title={rec.changeName}>
                        {rec.changeName}
                      </span>
                    </td>
                    <td data-label="Scope">{rec.scope}</td>
                    <td data-label="Stage">{rec.currentStep}</td>
                    <td data-label="Status">
                      <StatusBadge status={rec.status} />
                      {overdue && (
                        <span
                          className='badge badge--overdue'
                          style={{ marginLeft: 4 }}
                        >
                          Overdue
                        </span>
                      )}
                    </td>
                    <td data-label="Proposed Go-Live">{fmtDate(rec.proposedGolive)}</td>
                    <td data-label="Last Modified">{fmtDate(rec.lastModified)}</td>
                    <td data-label="" className='action-cell'>
                      <Link
                        to={action.url}
                        className={`btn btn--sm ${rec.status === 'Closed' ? 'btn--secondary' : 'btn--primary'}`}
                      >
                        {rec.status === 'Closed' ? 'View' : 'Action'}
                        <i
                          className='fa-solid fa-arrow-right'
                          aria-hidden='true'
                        />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── Compliance Dashboard view ──────────────────────────────────────────────────
function ComplianceDashboardView({
  onSwitchToMyChanges,
}: {
  onSwitchToMyChanges: () => void;
}) {
  const api = useApiClient();
  const { showToast } = useToast();
  const [records, setRecords] = useState<ChangeRequest[]>([]);
  const [filtered, setFiltered] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterScope, setFilterScope] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  useEffect(() => {
    api
      .get<ChangeRequest[]>('/changes')
      .then((data) => {
        setRecords(data);
        setFiltered(data);
      })
      .catch((err) => showToast((err as Error).message, 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const statusGroups: Record<string, string[]> = {
      active: [
        'Awaiting Stakeholder Notification',
        'Submitted',
        'Stakeholder Review',
        'Planning',
        'Plan Submitted',
        'Pending Sign-Off',
        'Ready for Approval',
        'Walkthrough Requested',
      ],
      closed: ['Closed'],
      rejected: ['Rejected', 'Rejection Review'],
      'pending-approval': [
        'Ready for Approval',
        'Walkthrough Requested',
        'Plan Submitted',
      ],
      'in-implementation': [
        'Approved',
        'In Implementation',
        'Implementation Complete',
      ],
      'pending-closure': ['Under Review', 'Pending Sponsor Closure'],
    };
    const result = records.filter((r) => {
      if (
        filterStatus &&
        !(statusGroups[filterStatus] ?? []).includes(r.status)
      )
        return false;
      if (filterScope && r.scope !== filterScope) return false;
      if (
        filterFrom &&
        r.dateSubmitted &&
        new Date(r.dateSubmitted) < new Date(filterFrom)
      )
        return false;
      if (
        filterTo &&
        r.dateSubmitted &&
        new Date(r.dateSubmitted) > new Date(filterTo + 'T23:59:59')
      )
        return false;
      return true;
    });
    setFiltered(result);
  }, [records, filterStatus, filterScope, filterFrom, filterTo]);

  const activeStatuses = [
    'Awaiting Stakeholder Notification',
    'Submitted',
    'Stakeholder Review',
    'Planning',
    'Plan Submitted',
    'Pending Sign-Off',
    'Ready for Approval',
    'Walkthrough Requested',
  ];
  const implStatuses = [
    'Approved',
    'In Implementation',
    'Implementation Complete',
  ];
  const closureStatuses = ['Under Review', 'Pending Sponsor Closure'];

  return (
    <>
      <div className='page-header'>
        <div className='page-header__title'>
          <h1>Change Register</h1>
          <p>
            Organisation-wide view of all change requests and their current
            status.
          </p>
        </div>
        <div className='page-header__actions'>
          <button className='btn btn--secondary' onClick={onSwitchToMyChanges}>
            <i className='fa-solid fa-user' aria-hidden='true' />
            My Changes
          </button>
          <Link to='/submit-change' className='btn btn--primary'>
            <i className='fa-solid fa-plus' aria-hidden='true' />
            New Change Request
          </Link>
        </div>
      </div>

      <div
        className='stat-grid'
        style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))' }}
      >
        <StatCard
          label='Total Changes'
          value={loading ? '…' : records.length}
        />
        <StatCard
          label='Active'
          value={
            loading
              ? '…'
              : records.filter((r) => activeStatuses.includes(r.status)).length
          }
          variant='primary'
        />
        <StatCard
          label='Pending Approval'
          value={
            loading
              ? '…'
              : records.filter((r) =>
                  [
                    'Ready for Approval',
                    'Walkthrough Requested',
                    'Plan Submitted',
                  ].includes(r.status),
                ).length
          }
          variant='warn'
        />
        <StatCard
          label='In Implementation'
          value={
            loading
              ? '…'
              : records.filter((r) => implStatuses.includes(r.status)).length
          }
          variant='secondary'
        />
        <StatCard
          label='Pending Closure'
          value={
            loading
              ? '…'
              : records.filter((r) => closureStatuses.includes(r.status)).length
          }
          variant='danger'
        />
        <StatCard
          label='Closed This Month'
          value={
            loading
              ? '…'
              : records.filter(
                  (r) => r.status === 'Closed' && isThisMonth(r.closureDate),
                ).length
          }
          variant='accent'
        />
      </div>

      {/* Filters */}
      <div className='filter-bar' role='search' aria-label='Filter changes'>
        <div className='filter-bar__group'>
          <label className='filter-bar__label' htmlFor='filter-status'>
            Status
          </label>
          <select
            id='filter-status'
            className='filter-bar__control form-control'
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value=''>All</option>
            <option value='active'>Active</option>
            <option value='closed'>Closed</option>
            <option value='rejected'>Rejected</option>
            <option value='pending-approval'>Pending Approval</option>
            <option value='in-implementation'>In Implementation</option>
            <option value='pending-closure'>Pending Closure</option>
          </select>
        </div>
        <div className='filter-bar__group'>
          <label className='filter-bar__label' htmlFor='filter-scope'>
            Scope
          </label>
          <select
            id='filter-scope'
            className='filter-bar__control form-control'
            value={filterScope}
            onChange={(e) => setFilterScope(e.target.value)}
          >
            <option value=''>All</option>
            <option value='Single Dept'>Single Dept</option>
            <option value='Multi Dept'>Multi Dept</option>
            <option value='Org-Wide'>Org-Wide</option>
          </select>
        </div>
        <div className='filter-bar__group'>
          <label className='filter-bar__label' htmlFor='filter-from'>
            From
          </label>
          <input
            type='date'
            id='filter-from'
            className='filter-bar__control'
            value={filterFrom}
            onChange={(e) => setFilterFrom(e.target.value)}
          />
        </div>
        <div className='filter-bar__group'>
          <label className='filter-bar__label' htmlFor='filter-to'>
            To
          </label>
          <input
            type='date'
            id='filter-to'
            className='filter-bar__control'
            value={filterTo}
            onChange={(e) => setFilterTo(e.target.value)}
          />
        </div>
        <button
          className='btn btn--secondary btn--sm'
          onClick={() => {
            setFilterStatus('');
            setFilterScope('');
            setFilterFrom('');
            setFilterTo('');
          }}
        >
          <i className='fa-solid fa-xmark' aria-hidden='true' /> Clear
        </button>
      </div>

      {/* Table */}
      <div className='section-panel'>
        <div className='section-panel__header'>
          <h2 className='section-panel__title'>All Changes</h2>
          <span className='badge badge--muted'>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div
          className='table-wrapper'
          style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}
        >
          <table className='table' aria-label='All change requests'>
            <thead>
              <tr>
                <th>Change ID</th>
                <th>Change Name</th>
                <th>Change Lead</th>
                <th>Sponsor</th>
                <th>Scope</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9}>
                    <div
                      className='loading-state'
                      style={{ padding: '40px 24px' }}
                    >
                      <div
                        className='spinner'
                        role='status'
                        aria-label='Loading'
                      />
                      <span>Loading…</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <div
                      className='empty-state-inner'
                      style={{ padding: '48px 24px' }}
                    >
                      <i className='fa-solid fa-inbox' aria-hidden='true' />
                      <p>No records match the current filters.</p>
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map((rec) => {
                const overdue = isOverdue(rec);
                return (
                  <tr key={rec.id}>
                    <td data-label="Change ID">
                      <span className='change-id-mono'>{rec.changeId}</span>
                    </td>
                    <td data-label="Change Name">
                      <span className='truncate' title={rec.changeName}>
                        {rec.changeName}
                      </span>
                    </td>
                    <td data-label="Change Lead">{rec.leadName}</td>
                    <td data-label="Sponsor">{rec.sponsorName}</td>
                    <td data-label="Scope">{rec.scope}</td>
                    <td data-label="Stage" style={{ fontSize: '0.8125rem' }}>{rec.currentStep}</td>
                    <td data-label="Status">
                      <StatusBadge status={rec.status} />
                      {overdue && (
                        <>
                          <br />
                          <span
                            className='badge badge--overdue'
                            style={{ marginTop: 4 }}
                          >
                            Overdue
                          </span>
                        </>
                      )}
                    </td>
                    <td data-label="Submitted">{fmtDate(rec.dateSubmitted)}</td>
                    <td data-label="" className='action-cell'>
                      <Link
                        to={`/change-detail?cid=${encodeURIComponent(rec.id)}`}
                        className='btn btn--secondary btn--sm'
                      >
                        View{' '}
                        <i
                          className='fa-solid fa-arrow-right'
                          aria-hidden='true'
                        />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function Home() {
  const { userEmail, isComplianceOfficer } = useAuth();
  const [view, setView] = useState<'my' | 'compliance'>('my');

  return (
    <Layout>
      {view === 'my' ? (
        <MyChangesView
          email={userEmail}
          isComplianceOfficer={isComplianceOfficer}
          onSwitchToCompliance={() => setView('compliance')}
        />
      ) : (
        <ComplianceDashboardView onSwitchToMyChanges={() => setView('my')} />
      )}
    </Layout>
  );
}
